import * as SHA256 from 'crypto-js/sha256';
import { auto } from 'angular';

import type { I18nService } from '../i18n/i18n.service';
import type { ConfigService } from '../App/services/mx-config-service';
import type { EventService } from '../App/services/event-service';
import { EventCodes } from '../App/model/event-codes';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { AuthBehaviorProviderService } from '../Auth/mx-auth-behavior-provider-service';
import { Telemetry } from './Telemetry';
import type { TrackOptions } from './telemetry.contracts';
import type { DataStorage } from '../App/services/mx-data-storage';
import { SystemStorageType } from '../App/model/system-storage-types';
import type { IDataProvider } from '../App/services/mx-data-storage';
import type { UserProfileService } from '../App/services/user-profile-service';
import type { MxNavigationService } from '../Shell/Navigation/mx-navigation-service';

const signInStorageKey = 'mx.telemetry.signIn';

export class TelemetryService {
	static $inject = [
		'mx.shell.Config',
		'mx.shell.InternationalizationService',
		'mx.shell.EventService',
		'mx.shell.AppsService',
		'mx.shell.AuthBehaviorProviderService',
		'mx.shell.DataStorage',
		'mx.shell.UserProfileService',
		'$injector',
	];

	private telemetry: Telemetry;

	private localStorage: IDataProvider;

	private isIdentified = false;
	private applicationId?: string;
	private applicationTitle?: string;

	private preIdentityEvents: Omit<TrackOptions, 'applicationTitle' | 'applicationId'>[] = [];

	constructor(
		private readonly config: ConfigService,
		private readonly i18nService: I18nService,
		private readonly eventService: EventService,
		private readonly applicationService: MxApplicationService,
		private readonly authBehaviour: AuthBehaviorProviderService,
		private readonly dataStorage: DataStorage,
		private readonly userProfileService: UserProfileService,
		private readonly $injector: auto.IInjectorService
	) {
		this.telemetry = new Telemetry({
			token: this.config.get('telemetryToken'),
			productVersion: this.config.get('productVersion'),
			envId: this.config.get('envId'),
			envType: this.config.get('envType'),
			licenseId: this.config.get('licenseId'),
			customerName: this.config.get('customerName'),
			customerId: this.config.get('customerId'),
			debugTelemetry: this.config.get('debugTelemetry') || false,
			language: this.i18nService.currentLanguage?.code,
		});

		this.localStorage = this.dataStorage.getStorage(SystemStorageType.LocalStorage);

		if (!this.telemetry.enabled) {
			return;
		}

		this.authBehaviour.onSessionEstablished(async ({ isNew }: { isNew: boolean }) => {
			if (isNew) {
				this.localStorage.setAsObject(signInStorageKey, isNew);
			}
		});

		this.authBehaviour.onBeforeSessionDestroy(() => {
			this.track({
				eventName: 'User Signed Out',
			});

			this.telemetry.reset();
		});

		this.eventService.subscribeOnce(EventCodes.ShellAppInitialized, async () => {
			const isNew = this.localStorage.getAsObject(signInStorageKey);
			this.localStorage.remove(signInStorageKey);

			await this.identify();

			if (isNew) {
				this.trackAuth({
					eventName: 'User Signed In',
				});
			}
		});
	}

	public async init() {
		if (this.telemetry.enabled) {
			this.eventService.subscribeGlobal(EventCodes.TrackTelemetry, (event, trackData: TrackOptions) => {
				this.trackAuth(trackData);
			});
		}
	}

	private async identify() {
		const profile = await this.userProfileService.get();

		const { value: applicationId, name: applicationTitle } = await this.applicationService.getCurrentApplication();
		this.applicationId = applicationId;
		this.applicationTitle = applicationTitle;

		this.telemetry.updateConfig({
			language: this.i18nService.currentLanguage?.code,
			roles: profile.invariantRoleNames,
		});

		this.telemetry.identify(
			SHA256(`${profile.objectId || profile.id}aBra8fpqwWn5ka${this.config.get('envId')}`.toLowerCase())
				.toString()
				.toLowerCase()
		);
		this.isIdentified = true;
		this.preIdentityEvents.forEach((event) => {
			this.track(event);
		});
		this.preIdentityEvents = [];
	}

	private track(trackOptions: Omit<TrackOptions, 'applicationTitle' | 'applicationId'>) {
		try {
			const { id: navigationId, technicalName: navigationTitle } = (this.$injector.get('mx.shell.NavigationService') as MxNavigationService)?.currentItem || {};

			this.telemetry.track({
				...trackOptions,
				applicationId: this.applicationId,
				applicationTitle: this.applicationTitle,
				navigationId,
				navigationTitle,
			} as TrackOptions);
		} catch (e) {
			if (this.config.get('debugTelemetry')) {
				console.error(e);
			}
		}
	}

	private trackAuth(trackOptions: Omit<TrackOptions, 'applicationTitle' | 'applicationId'>) {
		if (this.isIdentified) {
			this.track(trackOptions);
		} else {
			this.preIdentityEvents.push(trackOptions);
		}
	}
}
